import type { Answer } from "../dynamicForm";
import type { Permission } from "../shared/permissions";
import type { SavedFiltersProps } from "../dynamicGrid/SavedFilters";
import type { UnansweredMandatorySection } from "../dynamicForm/DynamicFormProvider";
import { DynamicGrid } from "../dynamicGrid";
import type { DpiaStatus } from "./dpia.types";
import { hasErrorCode } from "./errors";
/** Create Registration */
export type CreateRegistrationPayload = { name: string; tenantIds: string[] };
export type CreatedRegistration = { id: string };

/** Add Invitee */
export type AddInviteePayload = { userId: string; message: string };

/** Update Registration */
export type UpdateRegistrationPayload = { name: string };

type RegistrationDpiaStatus = "waived" | "recommended" | "performed" | DpiaStatus;

/** Get registration by id */
export type RegistrationById = {
  id: string;
  name: string;
  answers: Answer[];
  status: RegistrationStatus;
  /** ISO formatted date */
  createdAt: string;
  /** ISO formatted date */
  updatedAt: string;
  invitees: Invitee[];
  connectedTenants: Array<ConnectedTenant>;
  permissions: readonly Permission[];
  responsible: { userId: string; email: string; firstName: string; lastName: string };
  autoDPIAPreAssessment?: {
    id: string;
    answers: Answer[];
    dpiaStatus?: RegistrationDpiaStatus;
    riskCriteria: string[];
  };
  organisationEditability?: { isCustomerManaged: boolean };
};

export type ConnectedTenant = {
  id: string;
  name: string;
  personalDataController?: { name: string; id: string } | null;
};

type Invitee = {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
};

export const REGISTRATION_STATUS_VARIANTS = ["draft", "done", "completion", "review", "audit"] as const;
type RegistrationStatusVariant = (typeof REGISTRATION_STATUS_VARIANTS)[number];

type RegistrationStatusCommon = { statusUpdated: string; isCompletelyAnswered?: boolean };
type RegistrationStatusNonAudit = { status: Exclude<RegistrationStatusVariant, "audit"> };
type RegistrationStatusAudit = {
  status: Extract<RegistrationStatusVariant, "audit">;
  auditStatus: "draft" | "done" | "completion" | "review";
};
export type RegistrationStatus = RegistrationStatusCommon & (RegistrationStatusNonAudit | RegistrationStatusAudit);

/** Get registration grid view */
export type RegistrationsGridView = {
  stringifiedFilters?: SavedFiltersProps["stringifiedFilters"];
  registrations: GridViewRegistration[];
};

export type GridViewRegistration = {
  registrationId: string;
  name: string;
  answers: Answer[];
  /** ISO formatted date */
  createdAt: string;
  /** ISO formatted date */
  updatedAt: string;
  status: RegistrationStatus;
  connectedTenants: Array<ConnectedTenant>;
  responsible: { userId: string; email: string; firstName: string; lastName: string };
  numberOfUsersWithAccess: number;
  dpiaStatus?: RegistrationDpiaStatus;
  connectedDpiaId?: string;
  isSharedFromCustomer?: boolean;
};

/** Update registration status */
export type StatusChangeRequest =
  | { status: Omit<RegistrationStatus["status"], "completion" | "audit"> }
  | { status: Extract<RegistrationStatus["status"], "completion">; additionalText: string }
  | {
      status: Extract<RegistrationStatus["status"], "audit">;
      auditStatus: "ongoing" | "done" | "completion" | "review";
      additionalText?: string;
    };

const UpdateRegistrationStatusErrorCodes = {
  unansweredMandatoryQuestions: "MANDATORY_QUESTION_IS_NOT_ANSWERED",
} as const;

export type UnansweredMandatoryQuestionsError = {
  errorCode: typeof UpdateRegistrationStatusErrorCodes.unansweredMandatoryQuestions;
  unansweredMandatoryQuestionSections: UnansweredMandatorySection[];
};

export const isUnansweredMandatoryQuestionsError = (error: unknown): error is UnansweredMandatoryQuestionsError =>
  hasErrorCode(error, UpdateRegistrationStatusErrorCodes.unansweredMandatoryQuestions);

/** Get users with access to the registration */
export type RegistrationPermission = Extract<Permission, `${string}:registration:${string}`>;

type HistoryTenant = {
  value: string;
  name: string;
  personalDataController?: ConnectedTenant["personalDataController"];
};

type MultiTenantsHistoryValue = { tenants: Array<HistoryTenant> | null };

type SingleTenantHistoryValueWrittenBeforeMultiTenants = HistoryTenant;

export type ConnectedTenantsHistoryValue =
  MultiTenantsHistoryValue | SingleTenantHistoryValueWrittenBeforeMultiTenants | null | undefined;

export const processorOf = (registration: { connectedTenants: ReadonlyArray<ConnectedTenant> }) =>
  registration.connectedTenants[0]?.personalDataController;

export const connectedTenantsFromHistory = (value: ConnectedTenantsHistoryValue): Array<HistoryTenant> => {
  if (!value) return [];

  return "tenants" in value ? (value.tenants ?? []) : [value];
};

export const ResponsibleAccessErrorCodes = {
  responsibleHasNoAccessToNewTenant: "RESPONSIBLE_DOES_NOT_HAVE_AN_ACCESS_TO_NEW_TENANT",
  youCannotBeResponsibleInEveryTenant: "YOU_CANNOT_BE_RESPONSIBLE_IN_EVERY_TENANT",
} as const;

export type ResponsibleAccessError = {
  errorCode: (typeof ResponsibleAccessErrorCodes)[keyof typeof ResponsibleAccessErrorCodes];
  tenantsResponsibleCannotAccess: Array<string>;
};

const isResponsibleAccessError = (error: unknown): error is ResponsibleAccessError =>
  (hasErrorCode(error, ResponsibleAccessErrorCodes.responsibleHasNoAccessToNewTenant) ||
    hasErrorCode(error, ResponsibleAccessErrorCodes.youCannotBeResponsibleInEveryTenant)) &&
  "tenantsResponsibleCannotAccess" in error &&
  Array.isArray(error.tenantsResponsibleCannotAccess) &&
  error.tenantsResponsibleCannotAccess.every(name => typeof name === "string");

export const responsibleAccessErrorWithCode = (error: unknown, errorCode: ResponsibleAccessError["errorCode"]) =>
  isResponsibleAccessError(error) && error.errorCode === errorCode ? error : null;

export type UserWithAccess = {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  registrationPermissions: RegistrationPermission[];
  isInvitee: boolean;
};

export type UsersWithAccessResponse = { usersWithPermissions: UserWithAccess[] };

/** Get my tenant hierarchy but only tenants with create registration permission */
export type TenantHierarchy = {
  id: string;
  organizationName: string;
  parentId: string | null;
  children: TenantHierarchy[];
  permissions: readonly Permission[];
  personalDataController?: { name: string; id: string } | null;
  isPersonalDataController?: boolean;
};

export const REGISTRATION_METADATA_FIELDS = {
  CREATED_AT: "createdAt",
  UPDATED_AT: "updatedAt",
  CONNECTED_TENANT: "connectedTenant",
  RESPONSIBLE: "responsible",
  USERS_WITH_ACCESS: "usersWithAccess",
  DPIA_STATUS: "dpiaStatus",
  STATUS: "status",
} as const;

export const REGISTRATION_PROCESSOR_METADATA_FIELDS = {
  CONNECTED_PERSONAL_DATA_CONTROLLER: "connectedPersonalDataController",
  CONNECTED_TENANT: "connectedTenant",
  CREATED_AT: "createdAt",
  NUMBER_OF_USERS_WITH_ACCESS: "numberOfUsersWithAccess",
  RESPONSIBLE: "responsible",
  STATUS: "status",
  TENANTS: "tenants",
  UPDATED_AT: "updatedAt",
  USERS_WITH_ACCESS: "usersWithAccess",
} as const;

export const IS_SHARED_FROM_CUSTOMER_FIELD = "isSharedFromCustomer";

export const RegistrationMetadataFields = [DynamicGrid.MAIN_FIELD, ...Object.values(REGISTRATION_METADATA_FIELDS)];
export const RegistrationProcessorMetadataFields = [
  DynamicGrid.MAIN_FIELD,
  ...Object.values(REGISTRATION_PROCESSOR_METADATA_FIELDS),
];
